import React, { useLayoutEffect, useRef, useState } from "react";

/**
 * Scroll-in reveal (fade + rise) with zero JS animation library.
 *
 * Previously wrapped framer-motion's `motion.div` / `whileInView`. That pulled
 * ~40 KB (gzipped) of animation runtime into the critical bundle and — worse —
 * rendered the hero headline at opacity 0 until the library had booted and
 * played a 0.5 s tween, which pushed Largest Contentful Paint out by well
 * over a second on mobile.
 *
 * Now:
 *   - anything already inside the viewport on mount is shown immediately, with
 *     no transition (so the LCP element paints as soon as React commits);
 *   - everything else starts hidden and transitions in via CSS when an
 *     IntersectionObserver first sees it (`once`), honouring
 *     `prefers-reduced-motion`.
 *
 * Same public API as before: `children`, `delay` (s), `y` (px), `className`,
 * `as` (tag name).
 */
const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export const Reveal = ({
  children,
  delay = 0,
  y = 16,
  className = "",
  as = "div",
  ...rest
}) => {
  const ref = useRef(null);
  // "instant" = visible from the first paint (above the fold, reduced motion,
  // or no IntersectionObserver support). "shown" = revealed after scrolling.
  const [state, setState] = useState("hidden");

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (prefersReducedMotion() || typeof IntersectionObserver === "undefined") {
      setState("instant");
      return undefined;
    }
    const rect = el.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    if (rect.top < vh && rect.bottom > 0) {
      setState("instant");
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setState("shown");
          io.disconnect();
        }
      },
      { rootMargin: "-60px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const style =
    state === "instant"
      ? undefined
      : {
          "--qv-reveal-y": `${y}px`,
          "--qv-reveal-delay": `${delay}s`,
        };
  const cls = [
    className,
    state === "instant" ? "" : "qv-reveal",
    state === "shown" ? "is-visible" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return React.createElement(
    as,
    { ref, className: cls || undefined, style, ...rest },
    children
  );
};

export default Reveal;
